import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import lodashUniqBy from 'lodash/fp/uniqBy';
import lodashConcat from 'lodash/fp/concat';
import lodashKeyBy from 'lodash/fp/keyBy';

import Navbar from 'client/components/Navbar';
import SrListingsMapViewManager from 'client/components/SrListingsMapViewManager';
import SrSwipeableListingCard from 'client/components/SrSwipeableListingCard';

import { USER_ROLE_ADMIN } from 'common/userRoles';
import { PROPERTY_TYPE_OPTION_APARTMENT } from 'common/propertyTypes';
import {
  LISTING_DATA_SOURCE_SMART_APARTMENT,
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_LOCKBOX_TYPE_LOCKBOX,
  LISTING_DEFAULT_COUNT_BY_MAP_REQUEST,
  LISTING_SR_BROKERAGE_MLS_NUMBER,
} from 'common/listingConstants';
import { TOUR_SHOWING_STATUS_FAILED } from 'common/tourConstants';
import {
  DATA_TABLE_SOURCE_ADMIN_TOURS,
  DATA_TABLE_SOURCE_USER_BOOKMARKS,
} from 'common/dataTableConstants';
import { SR_API_ACTION_GET_LISTING_LIST } from 'api/actionTypes';
import { SCREEN_ID_ADMIN_ADD_TO_TOUR } from 'client/constants/screenIds';

import {
  getMarkerShape,
  getMarkerIconConfig,
  getMarkerLabelConfig,
  GOOGLE_MAPS_MARKER_THEME_TOUR,
  GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED,
  GOOGLE_MAPS_MARKER_SHAPE_BOX,
} from 'client/helpers/googleMapsHelpers';
import {
  actionInitializeDataTableState,
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';
import withTransitionHook from 'client/helpers/withTransitionHook';
import branch from 'client/core/branch';

import MapViewManagerActions from 'client/actions/MapViewManager';

function getTourTableSettings({ match }) {
  const tourId = parseInt(match.params.tourId, 10);
  return {
    dataSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
    viewId: `addToTour-${tourId}`,
    count: 1,
    filter: {
      'tours.id': tourId,
    },
  };
}

function getUserBookmarkTableSettings({ userId }) {
  return {
    dataSource: DATA_TABLE_SOURCE_USER_BOOKMARKS,
    viewId: `addToTour-${userId}`,
    count: LISTING_DEFAULT_COUNT_BY_MAP_REQUEST,
    filter: {
      'userBookmarks.userId': userId,
    },
  };
}

function createMarkerShape({ tour }) {
  const tourListingIds = tour.listings.map(({ id }) => id);

  return ({ mapZoomLevel, listing }) => {
    return tourListingIds.includes(listing.id)
      ? GOOGLE_MAPS_MARKER_SHAPE_BOX
      : getMarkerShape(mapZoomLevel);
  };
}

function createMarkerConfig({ tour, user, userBookmarks }) {
  const tourListingIds = tour.listings.map(({ id }) => id);
  const userBookmarkListingIds = userBookmarks.map(({ id }) => id);
  const calculateMarkerShape = createMarkerShape({ tour });
  const showingsByListingId = lodashKeyBy('listingId', tour.showings);

  return ({ map, googleMaps, listing }) => {
    const isListingInTour = tourListingIds.includes(listing.id);
    const markerShape = calculateMarkerShape({ mapZoomLevel: map.getZoom(), listing });
    let markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING;

    if (isListingInTour) {
      const showing = showingsByListingId[listing.id];
      if (showing.status === TOUR_SHOWING_STATUS_FAILED) {
        markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED;
      } else {
        markerTheme = GOOGLE_MAPS_MARKER_THEME_TOUR;
      }
    } else if (userBookmarkListingIds.includes(listing.id)) {
      markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED;
    }

    return {
      icon: getMarkerIconConfig({
        markerShape,
        googleMaps,
        markerTheme: markerTheme,
      }),
      label: getMarkerLabelConfig({
        markerShape,
        listing,
        currentUser: user,
      }),
      zIndex: isListingInTour ? 10 : 1,
      optimized: false,
    };
  };
}

function createGoogleMapsConfig(mapState) {
  return (googleMaps) => {
    return {
      center: mapState.mapCenter,
      zoom: mapState.mapZoomLevel,
      gestureHandling: 'greedy',
      clickableIcons: false,
      zoomControlOptions: {
        position: googleMaps.ControlPosition.RIGHT_TOP,
      },
      disableDefaultUI: true,
    };
  };
}

function getMapListings(mapState) {
  const tourListingIds = mapState.tour.listings.map(({ id }) => id);
  const listings = lodashUniqBy(
    'id',
    lodashConcat(mapState.tour.listings, mapState.listings, mapState.userBookmarks),
  );

  return listings.filter((listing) => {
    if (tourListingIds.includes(listing.id)) {
      return true;
    }

    if (!mapState.onlyOnDemandListings) {
      return true;
    }

    return (
      listing.occupantType === LISTING_OCCUPANT_TYPE_VACANT &&
      listing.lockboxType === LISTING_LOCKBOX_TYPE_LOCKBOX
    );
  });
}

@withTransitionHook
@withScreenId(SCREEN_ID_ADMIN_ADD_TO_TOUR)
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScrollToTop
@branch((props) => ({
  mapState: ['mapViewById', props.match.params.tourId],
  listingPhotoResolution: ['listingPhotoResolution'],
}))
class AdminAddToTourPage extends Component {
  static propTypes = {
    mapState: PropTypes.object,
    listingPhotoResolution: PropTypes.number.isRequired,
  };

  componentDidMount() {
    this._getTourData();
  }

  _getTourData = () => {
    const { match, dispatch } = this.props;

    const tourTableSettings = getTourTableSettings({ match });
    dispatch(actionInitializeDataTableState, tourTableSettings);
    dispatch(actionDataTableLoadItems, {
      ...tourTableSettings,
      onSuccess: ({ items }) => {
        const tour = items[0];
        const user = tour.user;

        dispatch(MapViewManagerActions.newMapView, {
          mapViewId: tour.id,
          data: {
            tour,
            user,
            listings: [],
            userBookmarks: [],
            viewingListingId: null,
            onlyOnDemandListings: false,
            availableHomesNearMoveInDate: false,
            centerMarkers: true,
            onlySunroomListings: false,

            mapArea: null,
            mapZoomLevel: 11,
            mapCenter: {
              lat: 30.170061,
              lng: -97.749222,
            },
          },
        });

        this._getUserBookmarks({ tourId: tour.id, userId: user.id });
      },
    });
  };

  _onMapAreaChange = ({ mapArea }) => {
    const { dispatch, mapState } = this.props;
    const { tour } = mapState;

    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { mapArea },
    });
    dispatch(this._getHomes);
  };

  _onMapZoomChange = (mapZoomLevel) => {
    const { mapState, dispatch } = this.props;
    const { tour } = mapState;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { mapZoomLevel },
    });
  };

  _onMapCenterChange = (mapCenter) => {
    const { mapState, dispatch } = this.props;
    const { tour } = mapState;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { mapCenter },
    });
  };

  _onClickMap = () => {
    this._onSwipe();
  };

  _onClickListing = (viewingListing) => {
    const { mapState, dispatch } = this.props;
    const { tour } = mapState;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { viewingListing },
    });
  };

  _getUserBookmarks = ({ tourId, userId }) => {
    const { dispatch } = this.props;

    const userBookmarkTableSettings = getUserBookmarkTableSettings({ userId: userId });
    dispatch(actionInitializeDataTableState, userBookmarkTableSettings);
    dispatch(actionDataTableLoadItems, {
      ...userBookmarkTableSettings,
      onSuccess: ({ items }) => {
        const userBookmarks = items.filter(
          ({ dataSource }) => dataSource !== LISTING_DATA_SOURCE_SMART_APARTMENT,
        );
        dispatch(MapViewManagerActions.setValues, {
          mapViewId: tourId,
          values: {
            userBookmarks: userBookmarks,
            centerMarkers: false,
          },
        });
      },
    });
  };

  _getHomes = ({ state, apiClient }) => {
    const { tour, user, mapArea, availableHomesNearMoveInDate, onlySunroomListings } = state.get([
      'mapViewById',
      this.props.match.params.tourId,
    ]);

    apiClient.postWithState({
      action: SR_API_ACTION_GET_LISTING_LIST,
      state: state,
      payload: {
        page: 0,
        count: LISTING_DEFAULT_COUNT_BY_MAP_REQUEST,
        filters: {
          ...user.searchFilters,
          availableHomesNearMoveInDate,
          listingBrokerageMlsNumber: onlySunroomListings ? LISTING_SR_BROKERAGE_MLS_NUMBER : null,
          propertyTypes: user.searchFilters.propertyTypes.filter(
            (propertyType) => propertyType !== PROPERTY_TYPE_OPTION_APARTMENT,
          ),
          locationPoint: mapArea,
        },
      },
      onSuccess: ({ data }) => {
        MapViewManagerActions.setValues(
          { state },
          {
            mapViewId: tour.id,
            values: {
              listings: data,
              centerMarkers: false,
            },
          },
        );
      },
    });
  };

  _onSwipe = () => {
    const { mapState, dispatch } = this.props;
    const { tour } = mapState;
    const viewingListing = null;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { viewingListing },
    });
  };

  _toggleOnDemandListings = () => {
    const { mapState, dispatch } = this.props;
    const { tour } = mapState;
    const onlyOnDemandListings = !mapState.onlyOnDemandListings;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { onlyOnDemandListings },
    });
  };

  _toggleMoveInDateFilter = () => {
    const { mapState, dispatch } = this.props;
    const { tour } = mapState;
    const availableHomesNearMoveInDate = !mapState.availableHomesNearMoveInDate;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { availableHomesNearMoveInDate },
    });
    dispatch(this._getHomes);
  };

  _toggleOnlySunroomListingsFilter = () => {
    const { mapState, dispatch } = this.props;
    const { tour } = mapState;
    const onlySunroomListings = !mapState.onlySunroomListings;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: tour.id,
      values: { onlySunroomListings },
    });
    dispatch(this._getHomes);
  };

  render() {
    const { mapState, listingPhotoResolution } = this.props;

    let srListingsMapViewManagerDOM = null;
    let srSwipeableListingCardDOM = null;

    if (mapState) {
      const widgets = [];
      widgets.push(
        <div
          key="move-in-date"
          className={classNames('filterMoveInDate', {
            active: mapState.availableHomesNearMoveInDate,
          })}
          onClick={this._toggleMoveInDateFilter}
        >
          <span className="label">move-in date</span>
        </div>,
      );
      widgets.push(
        <div
          key="on-demand"
          className={classNames('filterOnDemand', { active: mapState.onlyOnDemandListings })}
          onClick={this._toggleOnDemandListings}
        >
          <span className="label">on-demand</span>
        </div>,
      );
      widgets.push(
        <div
          key="sunroom-listings"
          className={classNames('filterSunroomListings', { active: mapState.onlySunroomListings })}
          onClick={this._toggleOnlySunroomListingsFilter}
        >
          <span className="label">sunroom</span>
        </div>,
      );

      srListingsMapViewManagerDOM = (
        <SrListingsMapViewManager
          widgets={widgets}
          listings={getMapListings(mapState)}
          bookmarksByListingId={[]}
          mapZoomLevel={mapState.mapZoomLevel}
          mapCenter={mapState.mapCenter}
          centerMarkers={mapState.centerMarkers}
          getMarkerConfig={createMarkerConfig(mapState)}
          getMarkerShape={createMarkerShape(mapState)}
          getGoogleMapsConfig={createGoogleMapsConfig(mapState)}
          onMapAreaChange={this._onMapAreaChange}
          onMapZoomChange={this._onMapZoomChange}
          onMapCenterChange={this._onMapCenterChange}
          onClickMap={this._onClickMap}
          onClickListing={this._onClickListing}
        />
      );

      if (mapState.viewingListing) {
        srSwipeableListingCardDOM = (
          <SrSwipeableListingCard
            listing={mapState.viewingListing}
            currentUser={mapState.user}
            onSwipe={this._onSwipe}
            adminTourId={mapState.tour.id}
            photoResolution={listingPhotoResolution}
          />
        );
      }
    }

    return (
      <div className="page-agentDashboard">
        <Navbar redirectLogoTo="/admin/tours" />

        {srListingsMapViewManagerDOM}
        {srSwipeableListingCardDOM}
      </div>
    );
  }
}

AdminAddToTourPage.displayName = 'AdminAddToTourPage';

export default AdminAddToTourPage;
